// src/pages/LoginPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn } from 'lucide-react';
import { signInWithGoogle } from '../firebase';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const { currentUser, hasValidReferralCode } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Redirect based on user state
  React.useEffect(() => {
    if (currentUser) {
      if (hasValidReferralCode) {
        // If user already has a verified referral code, go directly to questions
        navigate('/questions');
      } else {
        // Otherwise, go to referral code page
        navigate('/referral');
      }
    }
  }, [currentUser, hasValidReferralCode, navigate]);
  
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
      // Auth state change will trigger redirect in useEffect
    } catch (error) {
      console.error("Login failed:", error);
      setError('登录失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };
  
  const goToWelcome = () => {
    navigate('/');
  };
  
  return (
    <>
      {/* Fixed header with logo */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm py-3 z-10">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <img src="/new-logo-blue.png" alt="Careers Compass" className="h-10 object-contain" />
          </div>
        </div>
      </header>

      {/* Add padding top to account for fixed header */}
      <div className="w-full max-w-md mx-auto mt-18">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 w-full">
          <h2 className="text-2xl font-bold text-center text-blue-600 mb-2">登录以继续</h2>
          <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-blue-600 mx-auto mb-6 rounded-full"></div>
          
          <p className="text-gray-600 text-center mb-8">
            请使用您的 Google 账号登录以继续评估。我们需要确认您的身份以保存您的评估结果。
          </p>
          
          {/* Error message */}
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-6 text-sm border border-red-100">
              {error}
            </div>
          )}
          
          {/* Google login button */}
          <button 
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-3 py-3 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
              <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z" />
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
            <span className="text-gray-700 font-medium">{loading ? '登录中...' : '使用 Google 账号登录'}</span>
          </button>
          
          <div className="mt-6 text-center">
            <button 
              onClick={goToWelcome}
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center justify-center mx-auto"
            >
              <ArrowLeft size={16} className="mr-1" />
              返回首页
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;